import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Message } from 'primereact/message';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { Editor } from 'primereact/editor';
import { storeService } from 'services/store.service';
import { showTostify } from 'utils/interfaces';
import { routes } from 'utils/routes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Calendar } from 'primereact/calendar';
import CustomToast from 'views/components/form-inputs/alert';
import { InputField, PhoneNumberField, RadioButton, SelectField, TextArea } from 'views/components/form-inputs';
import { categoryService } from 'services/categories.service';
import statesData from 'static.json'
import { parseISO } from 'date-fns';
import './style.scss'
import moment from 'moment';
import { generatePreviewUrl } from 'utils/common-functions';
import axios from 'axios';

const step2ValidationSchema = Yup.object({
  terms_conditions: Yup.string().required('Terms and conditions are required'),
  help: Yup.string().required('Help information is required'),
  about: Yup.string().required('About information is required'),
});

const step4ValidationSchema = Yup.object({
  user: Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phone_number1: Yup.string().required('Phone number 1 is required'),
    gender: Yup.string().required('Gender is required'),
  })
});

const step3ValidationSchema = Yup.object({});
const step5ValidationSchema = Yup.object({});


const initialValues = {
  title: "",
  phone_number1: "",
  phone_number2: "",
  email: "",
  type: '',
  listing_type: "",
  state: "",
  city: "",
  discount: "",
  discount_type: "",
  address1: "",
  address2: "",
  postcode: "",
  landmark: "",
  category_id: "",
  sub_category: "",
  terms_conditions: "",
  help: "",
  about: "",
  description: "",
  country: "",
  seo_title: "",
  seo_keywords: "",
  seo_description: "",
  hashtags: "",
  user: {
    first_name: "",
    last_name: "",
    phone_number1: "",
    gender: "male",
    email: "",
    username: "",
    password: "",
    confirm_password: "",
  },
  deal: {
    title: "",
    description: "",
    type: "",
    payment_type: "view_now",
    discount_code: "",
    status: "",
    start_date: "",
    end_date: "",
  },
  logo: null,
  banner: null,
  menus: null,
}

const AddStore = () => {
  const params = useParams()
  const id: any = params.id
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type'); // Returns "global"
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any>([]);
  const [cities, setCities] = useState([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [initialData, setInitialData] = useState({ ...initialValues, type: type });

  const stepperRef = useRef<any>(null);
  const toastRef = useRef<any>(null);

  const navigation = useNavigate()

  const step1ValidationSchema = Yup.object({
    category_id: Yup.string().required('Category is required'),
    sub_category: Yup.string().required('Subcategory is required'),
    title: Yup.string().required('Store Title is required'),
    email: Yup.string().email('Invalid email format').required('Store email is required'),
    phone_number1: Yup.string().required('Store number 1 is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    postcode: Yup.string().required('Postcode is required'),
    address1: Yup.string().required('Address is required'),
    user: Yup.object().shape({
      phone_number1: Yup.string().required('User phone number is required'),
      username: Yup.string().required('Username is required'),
      password: id ? Yup.string() : Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('New password is required'),
      confirm_password: id ? Yup.string() : Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm Password is required'),
    })
  });

  const step6ValidationSchema = Yup.object({
    deal: id ? Yup.object().shape({}) : Yup.object().shape({
      title: Yup.string().required('Title is required'),
      type: Yup.string().required('Type is required'),
      payment_type: Yup.string().required('Payment type is required'),
      discount_code: Yup.string().required('Discount code is required'),
      amount: Yup.string().required('Amount is required'),
      status: Yup.string().required('Status is required'),
    })
  });

  // Combine step schemas in an object or array (for easier validation selection)
  const validationSchemas = [
    step1ValidationSchema,
    step2ValidationSchema,
    step3ValidationSchema,
    step4ValidationSchema,
    step5ValidationSchema,
    step6ValidationSchema
  ];


  const formik: any = useFormik({
    initialValues: initialData,
    validationSchema: validationSchemas[currentStep],
    enableReinitialize: true,
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        const data = await storeService.createStore({ ...values, type: type });
        if (data.error === false) {
          if (values.banner) {
            submitFile(data.data.id)
          }
          showTostify(toastRef, 'success', data.message);
          navigation(routes.stores)
        } else {
          showTostify(toastRef, 'error', data.message);
        }
      } catch (err: any) {
        showTostify(toastRef, 'error', err.message);
      } finally {
        setLoading(false);
      }
    },
  });


  useEffect(() => {
    if (id) {
      setLoading(true)
      const getStoresDetails = async () => {
        const data = await storeService.getStoresDetails(id);
        if (!data.error) {
          setInitialData(data)
          setLoading(false);
        } else {
          showTostify(toastRef, 'error', data.message);
          setLoading(false);
        }
      };
      getStoresDetails();
    }
  }, [id]);

  const handleStateChange = (e: any) => {
    const selectedStateId = e.value;
    formik.setFieldValue('state', selectedStateId);

    const selectedState: any = statesData.states.find(state => state.short_label === selectedStateId);
    if (selectedState) {
      setCities(selectedState.cities);
      formik.setFieldValue('city', '');
    } else {
      setCities([]);
    }
  };

  useEffect(() => {
    categoryService.getCategories().then((data) => {
      setCategories(data.items)
    })
  }, [])

  // Go to next step if the current step is valid
  const handleNextClick = () => {
    // Trigger validation for the current step
    formik.validateForm().then((errors: any) => {
      if (Object.keys(errors).length === 0) {
        // If no errors, move to the next step
        setCurrentStep((prevStep) => Math.min(prevStep + 1, validationSchemas.length - 1));
        stepperRef.current.nextCallback(); // Move to the next step in the Stepper component
      }
    });
  };

  // Go to previous step
  const handlePrevClick = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
    stepperRef.current.prevCallback(); // Move to the previous step in the Stepper component
  };

  // Handle the file selection for menus (multiple files)
  const handleMenusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      if (formik.values.menus?.length > 0) {

        formik.setFieldValue('menus', [...formik.values.menus, ...files]); // Update Formik state with selected files
      } else {

        formik.setFieldValue('menus', files); // Update Formik state with selected files
      }
    }
  };

  const submitFile = async (id: any) => {
    if (formik.values.logo) {
      const data = new FormData();
      data.append('key', 'store_logo');
      data.append('entity_id', `${id}`);
      data.append('files', formik.values.menus);
      data.append('entity_type', "banner");

      try {
        const response = await axios.post(`${process.env.REACT_APP_BASE_URL}v1/admin/media`, data, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'bIESIcg42Wb9') }
        });
        showTostify(toastRef, 'success', response?.data?.message);
        formik.resetForm();
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error);
      }
    }
    if (formik.values.banner) {
      const data = new FormData();
      data.append('key', 'store_banner');
      data.append('entity_id', `${id}`);
      data.append('files', formik.values.menus);
      data.append('entity_type', "banner");

      try {
        const response = await axios.post(`${process.env.REACT_APP_BASE_URL}v1/admin/media`, data, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'bIESIcg42Wb9') }
        });
        showTostify(toastRef, 'success', response?.data?.message);
        formik.resetForm();
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error);
      }
    }
    if (formik.values.menus) {
      const data = new FormData();
      data.append('key', 'store_menus');
      data.append('entity_id', `${id}`);
      data.append('files', formik.values.menus);
      data.append('entity_type', "banner");

      try {
        const response = await axios.post(`${process.env.REACT_APP_BASE_URL}v1/admin/media`, data, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'bIESIcg42Wb9') }
        });
        showTostify(toastRef, 'success', response?.data?.message);
        formik.resetForm();
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error);
      }
    }
  }

  return (
    <>
      <section className='p-add-local-store stores shopkeeper-page'>
        <div className="container" style={{ width: "800px", margin: "auto" }}>
          <CustomToast ref={toastRef} />
          <form onSubmit={formik.handleSubmit} className=''>

            <Stepper ref={stepperRef}>
              <StepperPanel header="Store Details">
                <div className='border-round-2xl'>
                  <div className="p-4 border-bottom-1 border-primary-color">
                    <h2 className='text-start m-0'>Add Local Store</h2>
                  </div>

                  <div className="grid m-0 pt-8 pb-4 p-3 mt-2">
                    <div className="col-12 md:col-6 lg:col-6">
                      <InputField
                        className={formik.touched?.user?.username && formik.errors?.user?.username ? 'p-invalid' : ''}
                        name="user.username"
                        label="Username"
                        required={true}
                        value={formik.values?.user?.username}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.user?.username && formik.errors?.user?.username}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-6">
                      <div className="form-group mb-2">
                        <PhoneNumberField
                          value={formik.values?.user?.phone_number1}
                          label='Mobile'
                          required={true}
                          onBlur={formik.handleBlur}
                          onChange={(phone) => formik.setFieldValue('user.phone_number1', phone || '')}
                          error={formik.touched?.user?.phone_number1 && formik.errors?.user?.phone_number1}
                        />
                      </div>
                    </div>
                    {!id && <>

                      <div className="col-12 md:col-6 lg:col-6">
                        <InputField
                          name="user.password"
                          label="Password"
                          required={true}
                          value={formik.values.user?.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik?.touched?.user?.password && formik.errors?.user?.password}
                        />
                      </div>
                      <div className="col-12 md:col-6 lg:col-6">
                        <InputField
                          name="user.confirm_password"
                          label="Confirm Password"
                          required={true}
                          value={formik.values?.user?.confirm_password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik?.touched?.user?.confirm_password && formik.errors?.user?.confirm_password}
                        />
                      </div>
                    </>}
                    <div className="col-12 md:col-6 lg:col-6">
                      <SelectField
                        name="category_id"
                        label="Category"
                        value={formik.values.category_id}
                        onChange={(e) => formik.setFieldValue('category_id', e.value)}
                        options={categories.map((category: any) => ({
                          label: category.name,
                          value: category.id,
                        }))}
                        error={formik.touched.category_id && formik.errors.category_id}
                        required={true}
                        className="my-custom-class"
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-6">
                      <InputField
                        name="sub_category"
                        label="Sub Category"
                        required={true}
                        value={formik.values.sub_category}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.sub_category && formik.errors?.sub_category}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-6">
                      <InputField
                        name="title"
                        label="Store Title"
                        required={true}
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.title && formik.errors?.title}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-6">
                      <InputField
                        name="email"
                        label="Store Email"
                        required={true}
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched.email && formik.errors.email}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-6">
                      <div className="form-group mb-2">
                        <label htmlFor="last_name">Store Number 1 <span className='required-field'>*</span></label>
                        <PhoneNumberField
                          value={formik.values.phone_number1}
                          required={true}
                          // country="in"
                          onBlur={formik.handleBlur}
                          onChange={(phone) => formik.setFieldValue('phone_number1', phone || '')}
                        />
                        {formik.touched.phone_number1 && formik.errors.phone_number1 && (
                          <Message severity="error" text={formik.errors.phone_number1} />
                        )}
                      </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-6">
                      <div className="form-group mb-2">
                        <PhoneNumberField
                          label='Store Number 2'
                          value={formik.values.phone_number2}
                          // country="in"
                          onBlur={formik.handleBlur}
                          onChange={(phone) => formik.setFieldValue('phone_number2', phone || '')}
                        />
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="address1"
                        label="Address 1"
                        required={true}
                        value={formik.values.address1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.address1 && formik.errors?.address1}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="address2"
                        label="Address 2"
                        value={formik.values.address2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.address2 && formik.errors?.address2}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="landmark"
                        label="Landmark"
                        value={formik.values.landmark}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.landmark && formik.errors?.landmark}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 ">
                      <SelectField
                        name="state"
                        label="State"
                        value={formik.values.state}
                        onChange={handleStateChange}
                        options={statesData?.states?.map(state => ({ label: state.label, value: state.short_label }))}
                        error={formik.touched.state && formik.errors.state}
                        required={true}
                        className="my-custom-class"
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-4 ">
                      <SelectField
                        name="city"
                        label="City"
                        value={formik.values.city}
                        onChange={(e) => formik.setFieldValue('city', e.value)}
                        options={cities.map(city => ({ label: city, value: city }))}
                        error={formik.touched.city && formik.errors.city}
                        required={true}
                        disabled={cities.length === 0}
                        className="my-custom-class"
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="postcode"
                        label="Postcode"
                        required={true}
                        value={formik.values.postcode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.postcode && formik.errors?.postcode}
                      />
                    </div>
                  </div>
                </div>

                <div className='px-3'>
                  <div className="col-12 mt-auto">
                    <div className="flex justify-content-between mb-3">
                      <Button label="Prev" severity="danger" outlined className='primary' icon="pi pi-arrow-left"
                        onClick={handlePrevClick}
                        disabled={true} />
                      <Button
                        label={loading ? "Next" : "Next"}
                        type="submit"
                        className="primary"
                        onClick={handleNextClick}
                      />
                    </div>
                  </div>
                </div>
              </StepperPanel>

              <StepperPanel header="Store Info">
                <div className='border-round-2xl'>
                  <div className="p-4 border-bottom-1 border-primary-color">
                    <h2 className='text-start m-0'>Add Local Store</h2>
                  </div>

                  <div className="grid m-0 pt-8 pb-4 p-3 mt-2">
                    <div className="col-12">
                      <div className="form-group mb-2">
                        <label htmlFor="last_name">About Store<span className='required-field'>*</span></label>
                        <Editor
                          value={formik.values.about}
                          onTextChange={(e) => formik.setFieldValue('about', e.htmlValue || '')}
                          style={{ height: '100px' }}
                          placeholder="Enter here" onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.about && formik.errors.about && (
                          <Message severity="error" text={formik.errors.about} />
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-2">
                        <label htmlFor="terms_conditions">Store Terms & Conditions<span className='required-field'>*</span></label>
                        <Editor
                          value={formik.values.terms_conditions}
                          onTextChange={(e) => formik.setFieldValue('terms_conditions', e.htmlValue || '')}
                          style={{ height: '100px' }}
                          placeholder="Enter here" onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.terms_conditions && formik.errors.terms_conditions && (
                          <Message severity="error" text={formik.errors.terms_conditions} />
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label htmlFor="help">Store Help<span className='required-field'>*</span></label>
                        <Editor
                          value={formik.values.help}
                          onTextChange={(e) => formik.setFieldValue('help', e.htmlValue || '')}
                          style={{ height: '100px' }}
                          placeholder="Enter here" onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.help && formik.errors.help && (
                          <Message severity="error" text={formik.errors.help} />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='px-3'>
                    <div className="col-12 mt-auto">
                      <div className="flex justify-content-between mb-3">
                        <Button label="Prev" severity="danger" outlined className='primary' icon="pi pi-arrow-left"
                          onClick={handlePrevClick} />

                        <Button
                          label={loading ? "Next" : "Next"}
                          type="submit"
                          className="primary"
                          onClick={handleNextClick}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </StepperPanel>

              <StepperPanel header="SEO Details">
                <div className='border-round-2xl'>
                  <div className="p-4 border-bottom-1 border-primary-color">
                    <h2 className='text-start m-0'>SEO Details</h2>
                  </div>
                  <div className="grid m-0 pt-8 pb-4 p-3 mt-2">
                    <div className="col-12 md:col-6 lg:col-12">
                      <InputField
                        name="seo_title"
                        label="SEO Title"
                        value={formik.values.seo_title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.seo_title && formik.errors?.seo_title}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-12">
                      <TextArea
                        name="seo_keywords"
                        label="SEO keywords"
                        value={formik.values.seo_keywords}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={5}
                        error={formik?.touched?.seo_keywords && formik.errors?.seo_keywords}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-12">
                      <TextArea
                        name="seo_description"
                        label="SEO Description"
                        value={formik.values.seo_description}
                        onChange={formik.handleChange}
                        rows={5}
                        error={formik?.touched?.seo_description && formik.errors?.seo_description}
                      />
                    </div>
                  </div>
                </div>

                <div className='px-3'>
                  <div className="col-12 mt-auto">
                    <div className="flex justify-content-between mb-3">
                      <Button label="Prev" severity="danger" outlined className='primary' icon="pi pi-arrow-left"
                        onClick={handlePrevClick} />
                      <Button
                        label={loading ? "Next" : "Next"}
                        type="submit"
                        className="primary"
                        onClick={handleNextClick}
                      />
                    </div>
                  </div>
                </div>
              </StepperPanel>

              <StepperPanel header="Shopkeeper Info">
                <div className='border-round-2xl'>
                  <div className="p-4 border-bottom-1 border-primary-color">
                    <h2 className='text-start m-0'>Add Shopkeeper Info</h2>
                  </div>
                  <div className="grid m-0 pt-8 pb-4 p-3 mt-2">
                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="user.first_name"
                        label="First Name"
                        required={true}
                        value={formik.values.user?.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.user?.first_name && formik.errors?.user?.first_name}
                      />
                    </div>

                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="user.last_name"
                        label="Last Name"
                        required={true}
                        value={formik.values.user?.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.user?.last_name && formik.errors?.user?.last_name}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-4">
                      <InputField
                        name="user.email"
                        label="Email"
                        required={true}
                        value={formik.values.user?.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik?.touched?.user?.email && formik.errors?.user?.email}
                      />
                    </div>
                    <div className="col-12 md:col-6 lg:col-6">
                      <div className="form-group mb-2">
                        <label htmlFor="phone_number1">Phone Number<span className='required-field'>*</span></label>
                        <PhoneNumberField
                          value={formik.values.user?.phone_number1}
                          required={true}
                          onBlur={formik.handleBlur}
                          onChange={(phone) => formik.setFieldValue('user.phone_number1', phone || '')}
                        />
                        {formik.touched.user?.phone_number1 && formik.errors?.user?.phone_number1 && (
                          <Message severity="error" text={formik.errors?.user?.phone_number1} />
                        )}
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-6">
                      <RadioButton
                        className="custom-form-check gender"
                        name="gender"
                        value={formik.values?.user?.gender}
                        onChange={(e: any) => formik.setFieldValue('user.gender', e.value)}
                        options={[
                          { label: 'Male', value: 'male' },
                          { label: 'Female', value: 'female' },
                          { label: 'Other', value: 'other' },
                        ]}
                        required={true}
                        label="Gender"
                        onBlur={formik.onBlur}
                        error={formik.touched?.user?.gender && formik.errors?.user?.gender}
                      />
                    </div>
                  </div>
                </div>

                <div className='px-3'>
                  <div className="col-12 mt-auto">
                    <div className="flex justify-content-between mb-3">
                      <Button label="Prev" severity="danger" outlined className='primary' icon="pi pi-arrow-left"
                        onClick={handlePrevClick} />
                      <Button
                        label={loading ? "Next" : "Next"}
                        type="submit"
                        className="primary"
                        onClick={handleNextClick}
                      />
                    </div>
                  </div>
                </div>
              </StepperPanel>

              <StepperPanel header="Logo & Banner ">
                <div className="border-round-2xl">
                  <div className="p-4 border-bottom-1 border-primary-color">
                    <h2 className="text-start m-0">Add Logo & Banner</h2>
                  </div>

                  <div className="grid m-0 pt-8 pb-4 p-3 mt-2">
                    {/* Logo Image Upload */}
                    <div className="col-6">
                      <div className="form-group mb-2">
                        <label className="label-form mb-1">Logo Image</label>
                      </div>

                      <ul className="fileupload p-list p-list-custom list-none flex flex-wrap gap-2 p-0 m-0">
                        {formik.values.logo && (
                          <li className="p-list-item">
                            <img
                              src={generatePreviewUrl(formik.values.logo)}
                              alt="Logo Preview"
                              width={80}
                              height={80}
                            />
                          </li>
                        )}
                        <li className="p-list-item">
                          <div className="avatar-upload">
                            <label htmlFor="logoUpload">
                              <i className="pi pi-cloud-upload" style={{ fontSize: '20px' }}></i>
                            </label>
                            <input
                              type="file"
                              id="logoUpload"
                              name="logo"
                              accept=".png, .jpg, .jpeg"
                              onChange={(e) => formik.setFieldValue("logo", e.currentTarget.files?.[0])}
                              style={{ visibility: 'hidden' }}
                            />
                          </div>
                        </li>
                      </ul>
                    </div>

                    {/* Banner Image Upload */}
                    <div className="col-6">
                      <div className="form-group mb-2">
                        <label className="label-form mb-1">Banner Image</label>
                      </div>

                      <ul className="fileupload p-list p-list-custom list-none flex flex-wrap gap-2 p-0 m-0">
                        {formik.values.banner && (
                          <li className="p-list-item">
                            <img
                              src={generatePreviewUrl(formik.values.banner)}
                              alt="Banner Preview"
                              width={80}
                              height={80}
                            />
                          </li>
                        )}
                        <li className="p-list-item">
                          <div className="avatar-upload">
                            <label htmlFor="bannerUpload">
                              <i className="pi pi-cloud-upload" style={{ fontSize: '20px' }}></i>
                            </label>
                            <input
                              type="file"
                              id="bannerUpload"
                              name="banner"
                              accept=".png, .jpg, .jpeg"
                              onChange={(e) => formik.setFieldValue("banner", e.currentTarget.files?.[0])}
                              style={{ visibility: 'hidden' }}
                            />
                          </div>
                        </li>
                      </ul>
                    </div>

                    {/* Menus Image Upload */}
                    <div className="col-12">
                      <div className="form-group mb-2">
                        <label className="label-form mb-1">Menus Image</label>
                      </div>

                      <ul className="fileupload p-list p-list-custom list-none flex flex-wrap gap-2 p-0 m-0">
                        {formik.values?.menus?.length > 0 &&
                          formik.values.menus.map((item: File, i: number) => (
                            <li className="p-list-item" key={i}>
                              <img
                                src={generatePreviewUrl(item)}
                                alt="Menus Preview"
                                width={80}
                                height={80}
                              />
                            </li>)
                          )}
                        <li className="p-list-item">
                          <div className="avatar-upload">
                            <label htmlFor="menusUpload">
                              <i className="pi pi-cloud-upload" style={{ fontSize: '20px' }}></i>
                            </label>
                            <input
                              type="file"
                              id="menusUpload"
                              name="menus"
                              accept=".png, .jpg, .jpeg"
                              multiple
                              onChange={handleMenusChange}
                              style={{ visibility: 'hidden' }}
                            />
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-3">
                    <div className="col-12 mt-auto">
                      <div className="flex justify-content-between mb-3">
                        <Button label="Prev" severity="danger" outlined className="primary" icon="pi pi-arrow-left" />
                        {id ? <Button
                          label="Next"
                          type="submit"
                          className="primary"
                          onClick={formik.handleSubmit}
                        /> :
                          <Button
                            label={loading ? "Loading..." : "Next"}
                            type="submit"
                            className="primary"
                            disabled={!formik.isValid || formik.isSubmitting || loading}
                          // onClick={() => stepperRef.current.nextCallback()}
                          />}
                      </div>
                    </div>
                  </div>
                </div>
              </StepperPanel>
              {!id &&
                <StepperPanel header="Deal Info">

                  <div className='border-round-2xl'>
                    <div className="p-4 border-bottom-1 border-primary-color">
                      <h2 className='text-start m-0'>Add Deal Info</h2>
                    </div>
                    <div className="grid m-0 pt-8 pb-4 p-3 mt-2">

                      <div className="col-12 md:col-6 lg:col-6">
                        <InputField
                          name="deal.title"
                          label="Deal Title"
                          required={true}
                          value={formik.values.deal?.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik?.touched?.deal?.title && formik.errors?.deal?.title}
                        />
                      </div>

                      <div className="col-12 md:col-6 lg:col-6">
                        <InputField
                          name="deal.amount"
                          type="number"
                          label="Saving upto"
                          required={true}
                          value={formik.values?.deal?.amount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik?.touched?.deal?.amount && formik.errors?.deal?.amount}
                        />
                      </div>
                      <div className="col-12 md:col-6 lg:col-6">
                        <RadioButton
                          className="custom-form-check"
                          name="type"
                          value={formik.values?.deal?.type}
                          onChange={(e: any) => formik.setFieldValue('deal.type', e.value)}
                          options={[
                            { label: 'Standard', value: 'standard' },
                            { label: 'Gift voucher', value: 'gift_voucher' },
                            { label: 'Exclusive', value: 'exclusive' },
                          ]}
                          required={true}
                          label="Deal Type"
                          error={formik.touched?.deal?.type && formik.errors?.deal?.type}
                        />
                      </div>

                      <div className="col-12 md:col-6 lg:col-6">
                        <RadioButton
                          className="custom-form-check"
                          name="payment_type"
                          value={formik.values?.deal?.payment_type}
                          onChange={(e: any) => formik.setFieldValue('deal.payment_type', e.value)}
                          options={[
                            { label: 'Call now', value: 'call_now' },
                            { label: 'Pay now', value: 'pay_now' },
                            { label: 'View now', value: 'view_now' },
                          ]}
                          required={true}
                          label="Payment Type"
                          error={formik.touched?.deal?.payment_type && formik.errors?.deal?.payment_type}
                        />
                      </div>
                      {formik.values?.deal?.payment_type === "call_now" &&
                        <div className="col-12 md:col-6 lg:col-6">
                          <InputField
                            name="deal.phone_number1"
                            label="Phone Number"
                            required={true}
                            value={formik.values?.deal?.phone_number1}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik?.touched?.deal?.phone_number1 && formik.errors?.deal?.phone_number1}
                          />
                        </div>}
                      {formik.values?.deal?.payment_type === "pay_now" &&
                        <div className="col-12 md:col-6 lg:col-6">
                          <InputField
                            name="deal.pay_amount"
                            label="Pay Amount"
                            required={true}
                            value={formik.values?.deal?.pay_amount}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik?.touched?.deal?.pay_amount && formik.errors?.deal?.pay_amount}
                          />
                        </div>}
                      {formik.values?.deal?.payment_type === "view_now" &&
                        <div className="col-12 md:col-6 lg:col-6">
                          <InputField
                            name="deal.discount_code"
                            label="Discount code"
                            required={true}
                            value={formik.values?.deal?.discount_code}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik?.touched?.deal?.discount_code && formik.errors?.deal?.discount_code}
                          />
                        </div>}

                      <div className="col-6">
                        <RadioButton
                          className="custom-form-check"
                          name="status"
                          value={formik.values?.deal?.status}
                          onChange={(e: any) => formik.setFieldValue('deal.status', e.value)}
                          options={[
                            { label: 'Active', value: 'active' },
                            { label: 'Draft', value: 'draft' },
                          ]}
                          required={true}
                          label="Status"
                          error={formik.touched?.deal?.status && formik.errors?.deal?.status}
                        />
                      </div>

                      <div className="col-12 md:col-6 lg:col-6">
                        <div className="form-group mb-2">
                          <label htmlFor="start_date">Start Date</label>
                          <Calendar
                            id="deal.start_date"
                            name="start_date"
                            value={formik.values?.deal?.start_date ? parseISO(formik.values?.deal?.start_date) : null}
                            onChange={(e) => {
                              if (e.value) {
                                const formattedDate = moment(e.value).toISOString();
                                formik.setFieldValue('deal.start_date', formattedDate);
                              } else {
                                formik.setFieldValue('deal.start_date', null);
                              }
                            }}
                            showIcon
                            minDate={new Date()}
                            placeholder="Enter here"
                            className={formik.touched?.deal?.start_date && formik.errors?.deal?.start_date ? 'p-invalid' : ''}
                          />
                          {formik.touched?.deal?.start_date && formik.errors?.deal?.start_date && (
                            <Message severity="error" text={formik.errors?.deal?.start_date} />
                          )}
                        </div>
                      </div>

                      <div className="col-12 md:col-6 lg:col-6">
                        <div className="form-group mb-2">
                          <label htmlFor="end_date">End Date</label>
                          <Calendar
                            id="end_date"
                            name="end_date"
                            value={formik.values?.deal?.end_date ? parseISO(formik.values?.deal?.end_date) : null}
                            onChange={(e) => {
                              if (e.value) {
                                const formattedDate = moment(e.value).toISOString();
                                formik.setFieldValue('deal.end_date', formattedDate);
                              } else {
                                formik.setFieldValue('deal.end_date', null);
                              }
                            }}
                            minDate={new Date()}
                            showIcon
                            placeholder="Enter here"
                            className={formik.touched?.deal?.end_date && formik.errors?.deal?.end_date ? 'p-invalid' : ''}
                          />
                          {formik.touched?.deal?.end_date && formik.errors?.deal?.end_date && (
                            <Message severity="error" text={formik.errors?.deal?.end_date} />
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-group mb-2">
                          <label htmlFor=".title">Description
                            {/* <span className='required-field'>*</span> */}
                          </label>
                          <Editor
                            value={formik.values?.description}
                            onTextChange={(e) => formik.setFieldValue('description', e.htmlValue || '')}
                            style={{ height: '100px' }}
                            placeholder="Enter here"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched?.description && formik.errors?.description && (
                            <Message severity="error" text={formik.errors?.description} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='px-3'>
                    <div className="col-12 mt-auto">
                      <div className="flex justify-content-between mb-3">
                        <Button label="Prev" severity="danger" outlined className='primary' icon="pi pi-arrow-left" onClick={() => stepperRef.current.prevCallback()} />
                        <Button
                          label={loading ? "Loading..." : "Next"}
                          type="submit"
                          className="primary"
                          disabled={!formik.isValid || formik.isSubmitting || loading}
                        // onClick={() => stepperRef.current.nextCallback()}
                        />
                      </div>
                    </div>
                  </div>
                </StepperPanel>}
            </Stepper>
          </form>
        </div>
      </section >
    </>
  );
};

export default AddStore;

